import moment from "moment-timezone";

import type {PropertyRuleTimeRange} from "@/src/modules/properties/types";

import {buildDaysFromRange} from "./build-days-from-range";
import {
  hasSafelistingNightCoverageStarted,
  isSafelistingAnchorDateSelectable,
} from "./compute-safelisting-coverage-preview";
import {getExcludedCalendarDatesFromDays} from "./excluded-dates-from-days";
import type {DatesRange, Safelisting, SafelistingFormValues} from "../types";

export type SafelistingEditDateLocks = {
  /** Covered anchor nights whose coverage has already started — must stay permitted. */
  lockedCoveredDates: string[];
  /** Excluded gap nights whose coverage window has started — cannot be un-excluded. */
  lockedExcludedDates: string[];
};

function lockAnchorDates(
  anchorDates: string[],
  rangeHours: PropertyRuleTimeRange,
  timeZone: string,
): string[] {
  const now = moment.tz(timeZone);
  return anchorDates
    .filter((anchor) => hasSafelistingNightCoverageStarted(anchor, rangeHours, timeZone, now))
    .sort();
}

/** Calendar-day fallback while property rule hours are still loading. */
function getSafelistingEditDateLocksByCalendarDay(
  safelisting: Safelisting,
): SafelistingEditDateLocks | null {
  const today = moment().format("YYYY-MM-DD");
  const lockedCoveredDates = safelisting.days
    .map((d) => d.date)
    .filter((date) => date < today)
    .sort();
  const originalExcluded = getExcludedCalendarDatesFromDays(safelisting.days);
  const lockedExcludedDates = originalExcluded.filter((date) => date < today).sort();
  if (lockedCoveredDates.length === 0 && lockedExcludedDates.length === 0) {
    return null;
  }
  return {lockedCoveredDates, lockedExcludedDates};
}

export function getSafelistingEditDateLocks(
  safelisting: Safelisting | null | undefined,
  rangeHours: PropertyRuleTimeRange | null | undefined,
  timeZone: string,
): SafelistingEditDateLocks | null {
  if (!safelisting?.days?.length) return null;
  if (!rangeHours) {
    return getSafelistingEditDateLocksByCalendarDay(safelisting);
  }

  const coveredAnchors = safelisting.days.map((d) => d.date);
  const lockedCoveredDates = lockAnchorDates(coveredAnchors, rangeHours, timeZone);
  const originalExcluded = getExcludedCalendarDatesFromDays(safelisting.days);
  const lockedExcludedDates = lockAnchorDates(originalExcluded, rangeHours, timeZone);

  if (lockedCoveredDates.length === 0 && lockedExcludedDates.length === 0) {
    return null;
  }
  return {lockedCoveredDates, lockedExcludedDates};
}

export function rangePreservesLockedCoverage(
  datesRange: DatesRange | undefined,
  excludedDates: string[],
  lockedCoveredDates: string[],
): boolean {
  if (lockedCoveredDates.length === 0) return true;
  const from = datesRange?.from;
  const to = datesRange?.to;
  if (!from || !to) return false;
  const covered = buildDaysFromRange({
    dates_range: {from, to},
    excluded_dates: excludedDates,
  });
  const coveredSet = new Set(covered);
  return lockedCoveredDates.every((date) => coveredSet.has(date));
}

export function preservesLockedExcludedDates(
  excludedDates: string[],
  lockedExcludedDates: string[],
): boolean {
  if (lockedExcludedDates.length === 0) return true;
  const set = new Set(excludedDates);
  return lockedExcludedDates.every((date) => set.has(date));
}

export function validateSafelistingEditDateLocks(
  values: SafelistingFormValues,
  locks: SafelistingEditDateLocks | null,
): string | null {
  if (!locks) return null;
  if (
    !rangePreservesLockedCoverage(values.dates_range, values.excluded_dates, locks.lockedCoveredDates)
  ) {
    return "Coverage that has already started cannot be removed from this permit.";
  }
  if (!preservesLockedExcludedDates(values.excluded_dates, locks.lockedExcludedDates)) {
    return "Past excluded dates cannot be changed on this permit.";
  }
  return null;
}

export function isLockedExcludedDate(
  dateStr: string,
  locks: SafelistingEditDateLocks | null,
): boolean {
  return locks?.lockedExcludedDates.includes(dateStr) ?? false;
}

export function isLockedCoveredCalendarDate(
  date: Date,
  locks: SafelistingEditDateLocks | null,
): boolean {
  if (!locks?.lockedCoveredDates.length) return false;
  const str = moment(date).format("YYYY-MM-DD");
  return locks.lockedCoveredDates.includes(str);
}

export function isCalendarDayBeforeTodayInPropertyTz(date: Date, timeZone: string): boolean {
  const dateStr = moment(date).format("YYYY-MM-DD");
  const todayStr = moment.tz(timeZone).format("YYYY-MM-DD");
  return dateStr < todayStr;
}

export function isSafelistingCalendarDateSelectable(
  date: Date,
  rangeHours: PropertyRuleTimeRange | null | undefined,
  timeZone: string,
): boolean {
  if (isCalendarDayBeforeTodayInPropertyTz(date, timeZone)) return false;
  if (!rangeHours) return true;
  const anchor = moment(date).format("YYYY-MM-DD");
  return isSafelistingAnchorDateSelectable(anchor, rangeHours, timeZone);
}

export function validateSafelistingNoPastCoverage(
  values: SafelistingFormValues,
  rangeHours: PropertyRuleTimeRange | null | undefined,
  timeZone: string,
  locks: SafelistingEditDateLocks | null = null,
): string | null {
  if (!rangeHours) return null;
  const from = values.dates_range?.from;
  const to = values.dates_range?.to;
  if (!from || !to) return null;

  const lockedCovered = new Set(locks?.lockedCoveredDates ?? []);
  const days = buildDaysFromRange({
    dates_range: {from, to},
    excluded_dates: values.excluded_dates,
  });
  const hasPastDay = days.some(
    (anchor) =>
      !lockedCovered.has(anchor) &&
      !isSafelistingAnchorDateSelectable(anchor, rangeHours, timeZone),
  );
  if (!hasPastDay) return null;
  return "One or more selected dates have coverage windows that have already ended.";
}
